import * as layout from '../markLayout.module.css';
import { MarkButton } from '../MarkControls';
import React from 'react';
import P from '@hero/ui-kit/typography/P';
import InlineLoader from '../../../../components/InlineLoader';
import type { DripStepTarget, Channel, TemplateLoadState } from '../../types';
import useDripTemplateSession from '../../hooks/useDripTemplateSession';
import useDripTemplateSessionTransport from '../../api/useDripTemplateSessionTransport';
import TemplateEditorShell from './TemplateEditorShell';
type Props = {
    target: DripStepTarget;
    channel: Channel;
    targetKind?: string | null;
    workspace?: boolean;
};
const DripTemplateLoader: React.FC<Props> = ({
    target,
    channel,
    targetKind,
    workspace = false
}) => {
    const transport = useDripTemplateSessionTransport();
    const { state, retry } = useDripTemplateSession(
        {
            drip_id: target.drip_id,
            version_number: target.version_number,
            step_id: target.step_id,
            channel
        },
        transport
    );
    if (state.status === 'loading') {
        return <InlineLoader />;
    }
    if (state.status === 'error') {
        return (
            <div
                role="alert"
                style={{
                    backgroundColor: 'var(--mark-danger-surface)',
                    color: 'var(--mark-danger)'
                }}
                className={layout.notice}
            >
                <P noDefaultMargin size="small">
                    Failed to load template data.
                </P>
                <MarkButton
                    type="button"
                    onClick={retry}
                    style={{
                        marginTop: '0.8rem'
                    }}
                >
                    Retry
                </MarkButton>
            </div>
        );
    }
    if (state.status === 'blocked') {
        return (
            <div
                role="alert"
                style={{
                    backgroundColor: 'var(--mark-danger-surface)',
                    color: 'var(--mark-danger)'
                }}
                className={layout.notice}
            >
                <P noDefaultMargin size="small">
                    {state.reason}
                </P>
            </div>
        );
    }
    // state.status === 'ready' — resolved once, from here on this render
    // owns the shell's identity; nothing about the query cache can change
    // templateId/initialTemplate again for this session.
    const templateLoadState: TemplateLoadState = state.templateId == null ? 'missing' : 'existing';
    return (
        <TemplateEditorShell
            key={`${target.drip_id}-${target.step_id}-${target.version_number ?? 'latest'}-${channel}`}
            target={target}
            channel={channel}
            markTemplate={state.initialTemplate}
            templateLoadState={templateLoadState}
            templateId={state.templateId}
            targetKind={targetKind}
            workspace={workspace && channel === 'email'}
        />
    );
};
export default DripTemplateLoader;
